// What the route card map draws, as GeoJSON — kept out of the component so
// the rules can be pinned without Mapbox:
//
// * the start and finish carry the map style's own start/finish icons, as on
//   the hike map;
// * the points between are the hike map's yellow numbered stage discs;
// * each section has a small "0–1" pill half way along it, which gives way to
//   the points when they crowd (the layer handles that), never the reverse;
// * the selected point gets a ring under its marker, and its section's pill
//   turns yellow — the same highlight as the table's chip.
import { pointAt, sectionLabelAt, type RoutePosition } from './routeLine';

export interface MapPoint { distanceMeters: number }

type PointFeature = {
  type: 'Feature';
  properties: Record<string, string | number | boolean>;
  geometry: { type: 'Point'; coordinates: [number, number] };
};

export interface FeatureCollection { type: 'FeatureCollection'; features: PointFeature[] }

export interface RouteCardMapFeatures {
  ends: FeatureCollection;
  points: FeatureCollection;
  sections: FeatureCollection;
  selection: FeatureCollection;
}

const collection = (features: PointFeature[]): FeatureCollection => ({ type: 'FeatureCollection', features });

const feature = (coordinates: [number, number], properties: PointFeature['properties']): PointFeature => ({
  type: 'Feature', properties, geometry: { type: 'Point', coordinates },
});

/** The section pill's label, "0–1": an en dash, as on the card and the PDF. */
export const sectionLabel = (index: number): string => `${index}–${index + 1}`;

export function routeCardMapFeatures(
  route: RoutePosition[],
  points: MapPoint[],
  selected: number,
): RouteCardMapFeatures {
  if (route.length < 2 || points.length < 2) {
    return { ends: collection([]), points: collection([]), sections: collection([]), selection: collection([]) };
  }
  const last = points.length - 1;
  const at = (index: number) => pointAt(route, points[index].distanceMeters);

  return {
    ends: collection([
      feature(at(0), { index: 0, end: 'start' }),
      feature(at(last), { index: last, end: 'finish' }),
    ]),
    points: collection(points.slice(1, -1).map((_, i) => feature(at(i + 1), {
      index: i + 1, number: i + 1, selected: i + 1 === selected,
    }))),
    sections: collection(points.slice(0, -1).map((point, i) => feature(
      sectionLabelAt(route, point.distanceMeters, points[i + 1].distanceMeters),
      { index: i + 1, label: sectionLabel(i), selected: i + 1 === selected },
    ))),
    selection: selected >= 1 && selected <= last
      ? collection([feature(at(selected), { index: selected, end: selected === last })])
      : collection([]),
  };
}
